/**
 * Millage Shape Progression Functions
 *
 * Provides navigation through the MASTER_SHAPE_LIST for best-fit processing.
 * These functions enable dynamic candidate generation by finding adjacent shapes
 * in the master shape sequence for optimization algorithms.
 */

import { MASTER_SHAPE_LIST } from './shapes/shape-registry';

/**
 * Gets the shape that comes after the given shapeId in the master list.
 * Used for forward progression in traditional search algorithms.
 *
 * @param shapeId The current shape ID (e.g., 'log-accel-13')
 * @returns The next shape ID or null if at the end of the list
 */
export function getShapeAfter(shapeId: string): string | null {
  const currentIndex = MASTER_SHAPE_LIST.findIndex((s) => s.id === shapeId);
  if (currentIndex === -1) {
    throw new Error(`Shape type "${shapeId}" not found in master shape list`);
  }
  if (currentIndex === MASTER_SHAPE_LIST.length - 1) {
    return null; // Already at the last shape
  }
  return MASTER_SHAPE_LIST[currentIndex + 1].id;
}

/**
 * Gets the shape that comes before the given shapeId in the master list.
 * Used for backward progression in bidirectional search algorithms.
 *
 * @param shapeId The current shape ID (e.g., 'log-accel-13')
 * @returns The previous shape ID or null if at the beginning of the list
 */
export function getShapeBefore(shapeId: string): string | null {
  const currentIndex = MASTER_SHAPE_LIST.findIndex((s) => s.id === shapeId);
  if (currentIndex === -1) {
    throw new Error(`Shape type "${shapeId}" not found in master shape list`);
  }
  if (currentIndex === 0) {
    return null; // Already at the first shape
  }
  return MASTER_SHAPE_LIST[currentIndex - 1].id;
}

/**
 * Gets information about a shape's position in the master list.
 * Useful for debugging and understanding search progression.
 *
 * @param shapeId The shape ID to analyze
 * @returns Shape position information
 */
export function getShapePosition(shapeId: string): {
  index: number;
  isFirst: boolean;
  isLast: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  family: string;
  totalShapes: number;
} {
  const currentIndex = MASTER_SHAPE_LIST.findIndex((s) => s.id === shapeId);
  if (currentIndex === -1) {
    throw new Error(`Shape type "${shapeId}" not found in master shape list`);
  }

  const shape = MASTER_SHAPE_LIST[currentIndex];
  const totalShapes = MASTER_SHAPE_LIST.length;

  return {
    index: currentIndex,
    isFirst: currentIndex === 0,
    isLast: currentIndex === totalShapes - 1,
    hasNext: currentIndex < totalShapes - 1,
    hasPrevious: currentIndex > 0,
    family: shape.family,
    totalShapes,
  };
}

/**
 * Gets a range of shapes around a given shapeId.
 * Useful for understanding the local context of a shape in the progression.
 *
 * @param shapeId The center shape ID
 * @param range The number of shapes to include before and after (default: 2)
 * @returns Array of shape IDs in the range
 */
export function getShapeRange(shapeId: string, range: number = 2): string[] {
  const currentIndex = MASTER_SHAPE_LIST.findIndex((s) => s.id === shapeId);
  if (currentIndex === -1) {
    throw new Error(`Shape type "${shapeId}" not found in master shape list`);
  }

  const startIndex = Math.max(0, currentIndex - range);
  const endIndex = Math.min(MASTER_SHAPE_LIST.length - 1, currentIndex + range);

  const result: string[] = [];
  for (let i = startIndex; i <= endIndex; i++) {
    result.push(MASTER_SHAPE_LIST[i].id);
  }

  return result;
}

/**
 * Validates that a shape ID exists in the master list.
 *
 * @param shapeId The shape ID to validate
 * @returns True if the shape exists, false otherwise
 */
export function isValidShapeId(shapeId: string): boolean {
  return MASTER_SHAPE_LIST.some((s) => s.id === shapeId);
}

/**
 * Gets all available shape IDs in order.
 * Useful for debugging and understanding the complete shape progression.
 *
 * @returns Array of all shape IDs in master list order
 */
export function getAllShapeIds(): string[] {
  return MASTER_SHAPE_LIST.map((s) => s.id);
}

/**
 * Gets shapes by family type.
 *
 * @param family The shape family ('log-decel', 'linear', 'log-accel')
 * @returns Array of shape IDs in that family
 */
export function getShapesByFamily(family: 'log-decel' | 'linear' | 'log-accel'): string[] {
  return MASTER_SHAPE_LIST.filter((s) => s.family === family).map((s) => s.id);
}
